import { Link } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";

/**
 * FarmersHubPage — shared shell for topical Farmers Pulse pages that are
 * being fleshed out incrementally. Provides tabbed navigation + a hero and
 * a "coming soon" section for feature areas that will land in later phases.
 */
export default function FarmersHubPage({
  testid,
  eyebrow,
  Icon,
  title,
  subtitle,
  intro,
  sections = [],
  callToAction,
}) {
  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332] min-h-screen pt-32 md:pt-36 pb-32" data-testid={testid}>
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <header className="mt-3">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
            {Icon && <Icon size={12} weight="fill" />} / {eyebrow}
          </div>
          <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="mt-2 font-serif-display italic text-[#3A3830] max-w-3xl">{subtitle}</p>
          )}
          {intro && (
            <p className="mt-3 text-sm md:text-base text-[#2A2724] max-w-3xl leading-relaxed">{intro}</p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <ListenTranslateBar
              text={[title, subtitle, intro].filter(Boolean).join(" · ")}
              testidPrefix={`${testid}-tools`}
            />
            <ShareButton
              title={title}
              text={subtitle || intro || title}
              hashtags={["FarmersPulse", "NaturePulse"]}
              variant="chip"
              shortKind="article"
              testid={`${testid}-share`}
            />
          </div>
        </header>

        <section className="mt-8 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sections.map((s, i) => (
            <div key={i} className="rounded-2xl border border-black/10 bg-white p-5" data-testid={`${testid}-section-${i}`}>
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {s.eyebrow || "Section"}
              </div>
              <div className="mt-1 font-serif-display text-xl leading-tight">{s.title}</div>
              {s.description && <p className="mt-2 text-sm text-[#3A3830]">{s.description}</p>}
              {s.bullets && (
                <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-[#3A3830]">
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
              {s.to && (
                <Link to={s.to} className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] hover:underline">
                  {s.linkLabel || "Open"} <ArrowRight size={10} weight="bold" />
                </Link>
              )}
              {s.badge && (
                <div className="mt-3 inline-block rounded-full bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                  {s.badge}
                </div>
              )}
            </div>
          ))}
        </section>

        {callToAction && (
          <section className="mt-10 rounded-3xl border border-black/10 bg-[#1B4332] text-[#F5EBDC] p-8 text-center">
            <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {callToAction.eyebrow}
            </div>
            <div className="mt-2 font-serif-display text-2xl md:text-3xl">{callToAction.title}</div>
            {callToAction.description && <p className="mt-2 text-sm text-[#E5D8C4] max-w-xl mx-auto">{callToAction.description}</p>}
            {callToAction.to && (
              <Link
                to={callToAction.to}
                data-testid={`${testid}-cta`}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
              >
                {callToAction.label} <ArrowRight size={11} weight="bold" />
              </Link>
            )}
          </section>
        )}
      </div>
    </main>
  );
}
